'use client';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatDateForUser } from '@/utils/formatters';
import { mergeTimeRanges } from '@/utils/availability-fit';
import { timeToMinutes } from '@/utils/time';
import { dayOfWeekMap } from '@/components/clubs/upsert-club/validations/validations';
import type { Availability } from '@/types/tournament.model';

type Slot = { startTime: string; endTime: string };

// A slot belongs to the bucket its start time falls in (bounds inclusive, in minutes).
const BUCKET_BOUNDS = [
  { key: 'morning', label: 'Mañana', from: '06:00', to: '11:59' },
  { key: 'midday', label: 'Mediodía', from: '12:00', to: '15:59' },
  { key: 'afternoon', label: 'Tarde', from: '16:00', to: '19:59' },
  { key: 'night', label: 'Noche', from: '20:00', to: '24:00' },
].map((b) => ({
  ...b,
  from: timeToMinutes(b.from),
  to: timeToMinutes(b.to),
}));

const bucketKeyFor = (startTime: string): string | null => {
  const m = timeToMinutes(startTime);
  return BUCKET_BOUNDS.find((b) => m >= b.from && m <= b.to)?.key ?? null;
};

const getTimeBuckets = (slots: Slot[], scheduleSlots?: Slot[]) => {
  const sorted = [...slots].sort(
    (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
  );
  const activeKeys =
    scheduleSlots &&
    new Set(scheduleSlots.map((s) => bucketKeyFor(s.startTime)));

  return BUCKET_BOUNDS.filter((b) => !activeKeys || activeKeys.has(b.key)).map(
    (b) => {
      const items = sorted.filter((s) => bucketKeyFor(s.startTime) === b.key);
      // The stretches the team actually picked ("17:00–20:45"), not the bucket's generic
      // bounds; a gap shows as two stretches rather than one range claiming the time between.
      const stretches = mergeTimeRanges(items).map(
        (s) => `${s.startTime}–${s.endTime}`,
      );
      return { key: b.key, label: b.label, items, stretches };
    },
  );
};

interface AvailabilityDayCardProps {
  date: string;
  dayOfWeek: Availability['dayOfWeek'];
  /** Empty when the team has no availability that day: every bucket renders red. */
  slots: Slot[];
  /** The tournament's slots for this day; when given, only the buckets it covers are shown. */
  scheduleSlots?: Slot[];
  isCompact: boolean;
  /** In compact mode, puts each bucket's range under its label instead of beside it. */
  stackBucketHeaders?: boolean;
  className?: string;
}

export function AvailabilityDayCard({
  date,
  dayOfWeek,
  slots,
  scheduleSlots,
  isCompact,
  stackBucketHeaders = false,
  className,
}: AvailabilityDayCardProps) {
  const buckets = getTimeBuckets(slots, scheduleSlots);
  const isStacked = isCompact && stackBucketHeaders;

  return (
    <div className={cn('rounded-lg border bg-white p-3', className)}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm font-medium text-gray-900">
          {formatDateForUser(date)}{' '}
          <span className="font-normal text-gray-500">
            ({dayOfWeekMap[dayOfWeek] ?? dayOfWeek})
          </span>
        </div>
        {!isCompact && (
          <Badge variant="outline" className="text-xs">
            {slots.length} horario{slots.length === 1 ? '' : 's'}
          </Badge>
        )}
      </div>

      <div
        className={cn(
          'grid gap-2',
          isCompact ? 'grid-cols-2 sm:grid-cols-4' : 'sm:grid-cols-2',
        )}
      >
        {buckets.map((b) => {
          const hasSlots = b.items.length > 0;
          return (
            <div
              key={b.key}
              className={cn(
                'rounded-lg border-2',
                hasSlots ? 'border-green-400' : 'border-red-400',
                isCompact ? 'p-2' : 'p-3',
              )}
            >
              <div
                className={cn(
                  'flex',
                  isCompact ? 'mb-0' : 'mb-2',
                  isStacked
                    ? 'flex-col items-start gap-2'
                    : 'flex-wrap items-baseline justify-between gap-x-2',
                )}
              >
                <div
                  className={cn(
                    'font-semibold text-gray-900',
                    isCompact ? 'text-xs' : 'text-sm',
                  )}
                >
                  {b.label}
                </div>
                {/* Expanded red buckets say "Sin horarios" in their body instead. */}
                {(hasSlots || isCompact) && (
                  <div
                    className={cn(
                      'whitespace-nowrap text-gray-600',
                      isCompact ? 'text-[10px]' : 'text-xs',
                      isStacked ? 'leading-tight' : 'text-right',
                    )}
                  >
                    {hasSlots
                      ? b.stretches.map((s) => <div key={s}>{s}</div>)
                      : 'Sin horarios'}
                  </div>
                )}
              </div>

              {!isCompact &&
                (hasSlots ? (
                  <div className="flex flex-wrap gap-2">
                    {b.items.map((t) => (
                      <span
                        key={`${b.key}-${t.startTime}-${t.endTime}`}
                        className="inline-flex items-center rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200"
                      >
                        {t.startTime}–{t.endTime}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Sin horarios</p>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
